import { z } from "zod";
import { Lunar, LunarYear, Solar } from "lunar-typescript";

export const kinds = ["parent", "task", "event", "group", "site"] as const;
export type Kind = (typeof kinds)[number];
export const datePattern = /^\d{4}-\d{2}-\d{2}$/;
export function validDate(s: string) {
  if (!datePattern.test(s)) return false;
  const d = new Date(`${s}T12:00:00Z`);
  return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === s;
}
export const dateSchema = z.string().refine(validDate, "日期格式或日期无效");
export const linkSchema = z.object({
  name: z.string().max(200),
  url: z.url().refine((s) => /^https?:\/\//i.test(s), "只允许 HTTP(S) 链接"),
});
export type Link = z.infer<typeof linkSchema>;
export const entitySchema = z.object({
  title: z.string().trim().min(1, "请输入标题").max(200),
  note: z.string().max(20000).default(""),
  links: z.array(linkSchema).max(30).default([]),
  done: z.boolean().default(false),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default("#5c82d9"),
  start: dateSchema.nullable().default(null),
  end: dateSchema.nullable().default(null),
  dateMode: z.enum(["auto", "manual"]).default("auto"),
  parentId: z.string().nullable().default(null),
  order: z.number().finite().default(0),
  calendarOrder: z.record(dateSchema, z.number().int().min(0)).default({}),
  calendarOrderUpdatedAt: z
    .record(dateSchema, z.number().int().min(0))
    .default({}),
  cardColumn: z.number().int().min(0).max(2).nullable().default(null),
  cardHeight: z.number().int().min(160).max(1000).nullable().default(null),
  archived: z.boolean().default(false),
  calendar: z.enum(["solar", "lunar"]).default("solar"),
  repeat: z.enum(["once", "yearly"]).default("once"),
  year: z.number().int().min(1900).max(2100).default(2026),
  month: z.number().int().min(1).max(12).default(1),
  day: z.number().int().min(1).max(31).default(1),
  leap: z.boolean().default(false),
  leapPolicy: z.enum(["normal", "skip"]).default("normal"),
  duration: z.number().int().min(1).max(366).default(1),
  icon: z.string().max(40).default("calendar"),
  url: z.string().max(4096).default(""),
  assetId: z.string().nullable().default(null),
});
export type EntityData = z.infer<typeof entitySchema>;
export type Entity = EntityData & {
  id: string;
  kind: Kind;
  revision: number;
  updatedAt: string;
  deletedAt: string | null;
  deleteBatch: string | null;
  builtin: boolean;
};
export type User = {
  id: string;
  username: string;
  displayName: string;
  role: "admin" | "user";
  timezone: string;
  mustChangePassword: boolean;
  active: boolean;
};
export type Holiday = {
  date: string;
  name: string;
  type: "public_holiday" | "transfer_workday";
};
export type Occurrence = {
  id: string;
  entityId: string;
  start: string;
  end: string;
  title: string;
  color: string;
  icon: string;
  links: Link[];
  repeat: "once" | "yearly";
};
export type Snapshot = { items: Entity[]; user: User };
export const passwordSchema = z.string().min(12, "密码至少 12 个字符").max(256);
export function addDays(date: string, n: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
export function daysBetween(a: string, b: string) {
  return Math.round(
    (Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400000,
  );
}
export function monday(date: string) {
  const w = new Date(`${date}T12:00:00Z`).getUTCDay();
  return addDays(date, -(w + 6) % 7);
}
export function todayIn(timezone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  return ["year", "month", "day"]
    .map((k) => parts.find((p) => p.type === k)!.value)
    .join("-");
}
export function lunarLabel(date: string) {
  const [y, m, d] = date.split("-").map(Number);
  const l = Solar.fromYmd(y, m, d).getLunar();
  const festivals = l.getFestivals();
  return {
    label:
      festivals[0] ||
      (l.getDay() === 1 ? `${l.getMonthInChinese()}月` : l.getDayInChinese()),
    festival: festivals.length > 0,
  };
}
export function dateToCalendar(date: string, calendar: "solar" | "lunar") {
  const [y, m, d] = date.split("-").map(Number);
  if (calendar === "solar") return { year: y, month: m, day: d, leap: false };
  const l = Solar.fromYmd(y, m, d).getLunar();
  return {
    year: l.getYear(),
    month: Math.abs(l.getMonth()),
    day: l.getDay(),
    leap: l.getMonth() < 0,
  };
}
function annualStart(e: EntityData, year: number): string | null {
  if (e.calendar === "solar") {
    const last = new Date(Date.UTC(year, e.month, 0)).getUTCDate();
    return `${year}-${String(e.month).padStart(2, "0")}-${String(Math.min(e.day, last)).padStart(2, "0")}`;
  }
  const ly = LunarYear.fromYear(year);
  let month = e.leap ? -e.month : e.month;
  let lm = ly.getMonth(month);
  if (!lm) {
    if (e.leapPolicy === "skip") return null;
    month = e.month;
    lm = ly.getMonth(month);
  }
  if (!lm) return null;
  return Lunar.fromYmd(year, month, Math.min(e.day, lm.getDayCount()))
    .getSolar()
    .toYmd();
}
export function occurrences(e: Entity, from: string, to: string): Occurrence[] {
  if (e.kind !== "event" || e.deletedAt) return [];
  const years =
    e.repeat === "once"
      ? [e.year]
      : Array.from(
          { length: Number(to.slice(0, 4)) - Number(from.slice(0, 4)) + 3 },
          (_, i) => Number(from.slice(0, 4)) - 1 + i,
        );
  return years
    .filter((y) => y >= e.year && y >= 1900 && y <= 2100)
    .flatMap((year) => {
      const start = annualStart(e, year);
      if (!start) return [];
      const end = addDays(start, e.duration - 1);
      if (end < from || start > to) return [];
      return [
        {
          id: `${e.id}:${year}`,
          entityId: e.id,
          start,
          end,
          title: e.title,
          color: e.color,
          icon: e.icon,
          links: e.links,
          repeat: e.repeat,
        },
      ];
    });
}
export function effectiveRange(parent: Entity, tasks: Entity[]) {
  if (parent.dateMode === "manual")
    return { start: parent.start, end: parent.end };
  const scheduled = tasks.filter(
    (t) => t.parentId === parent.id && !t.deletedAt && t.start,
  );
  return {
    start: scheduled.map((t) => t.start!).sort()[0] || null,
    end:
      scheduled
        .map((t) => t.end!)
        .sort()
        .at(-1) || null,
  };
}
export function overlaps(start: string, end: string, from: string, to: string) {
  return start <= to && end >= from;
}
// Saved ordering preferences are independent of the task list's order.
export function compareCalendarTasks(a: Entity, b: Entity, date: string) {
  return (
    (a.calendarOrder[date] ?? Number.MAX_SAFE_INTEGER) -
      (b.calendarOrder[date] ?? Number.MAX_SAFE_INTEGER) ||
    (a.start || "").localeCompare(b.start || "") ||
    (b.end || "").localeCompare(a.end || "") ||
    a.order - b.order ||
    a.id.localeCompare(b.id)
  );
}
export function validateRange(d: EntityData) {
  if (
    (d.start === null) !== (d.end === null) ||
    (d.start && d.end && d.start > d.end)
  )
    throw new Error("开始和结束日期必须同时填写，且结束不能早于开始");
}
export function shortDate(s: string) {
  return `${Number(s.slice(5, 7))}.${Number(s.slice(8, 10))}`;
}
